/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		height: '52px',
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		display: 'flex',
		alignItems: 'center',
		paddingLeft: token('space.150'),
		paddingRight: token('space.100'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
	iconContainer: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	content: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.100'),
		flex: 1,
	},
	buttonGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export type ThinkingCompleteProps = {
	hasSuggestions: boolean;
	onClose: () => void;
	onViewSuggestions: () => void;
};

export function ThinkingComplete({
	hasSuggestions,
	onClose,
	onViewSuggestions,
}: ThinkingCompleteProps): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.wrapper}>
			{hasSuggestions ? (
				<>
					<div css={styles.content}>
						<div css={styles.iconContainer}>
							<CheckMarkIcon label="" color={token('color.icon.success')} />
						</div>
						<Text>{formatMessage(aiSuggestionsMessages.thinkingCompleteReviewComplete)}</Text>
					</div>
					<div css={styles.buttonGroup}>
						<Button
							appearance="subtle"
							onClick={onClose}
							testId="ai-suggestions-thinking-complete-close-button"
						>
							{formatMessage(aiSuggestionsMessages.thinkingCompleteClose)}
						</Button>
						<Button
							appearance="primary"
							onClick={onViewSuggestions}
							testId="ai-suggestions-thinking-complete-view-suggestions-button"
						>
							{formatMessage(aiSuggestionsMessages.thinkingCompleteViewSuggestions)}
						</Button>
					</div>
				</>
			) : (
				<div css={styles.content}>
					<Text>{formatMessage(aiSuggestionsMessages.thinkingCompleteNoSuggestions)}</Text>
				</div>
			)}
		</div>
	);
}
